import { useEffect, useRef, useState, type ReactNode } from 'react';
// `Link` left with the footer's Terms link, which goes to the website now.
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { stopViewAs } from '../lib/adminApi.js';
import { LEGAL_URLS, NEW_TAB } from '../lib/brand.js';
import { holdsCurrent, isCurrent } from '../lib/navCurrent.js';
import { useMediaQuery, WIDE_NAV_QUERY } from '../lib/useMediaQuery.js';
import { useSession } from '../session/SessionProvider.js';
import type { ViewingAs } from '../session/types.js';

import { DropdownMenu } from './DropdownMenu.js';
import { MobileMenu } from './MobileMenu.js';
import { Button } from './ui.js';
import { Wordmark } from './Wordmark.js';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

/**
 * "Projects", not "Clients" — ruling A-2.
 *
 * An agency's own customers are their clients; what this portal holds is a
 * reusable set of order details for a business they order for. Calling those
 * "clients" put the agency's relationship words on our record and, worse,
 * implied you had to create one before you could order anything. The database
 * table keeps its `client_profiles` name: renaming it would be a destructive
 * migration for a word, and it is recorded as accepted residual naming in
 * PORTAL-BUILD-REPORT.md, on the same terms as the compose container names.
 */
const AGENCY_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/orders', label: 'Orders' },
  { to: '/sites', label: 'Sites' },
  { to: '/projects', label: 'Projects' },
  { to: '/credits', label: 'Credits' },
  // D-9b ruling 16: a general way in, so an agency does not have to find the
  // volume link on the buy page to reach a human. **Stays on the bar** under
  // the one-row ruling: buyers must always be able to find help.
  { to: '/contact?about=support', label: 'Support' },
];

/**
 * The agency's account menu, under its name - the one-row ruling.
 *
 * Feedback was linked at WAIT-B (defect 4) because nothing led to the form;
 * it moves into the menu, not off the page, and the phone panel lists it too.
 */
const AGENCY_ACCOUNT: NavItem[] = [
  { to: '/settings', label: 'Settings' },
  { to: '/contact?about=feedback', label: 'Feedback' },
];

/**
 * The operator's daily pages. D-13 put Sales second, beside the work queue;
 * Agencies closes the run because it is where view-as and offboarding start.
 */
const OPERATOR_NAV: NavItem[] = [
  { to: '/admin/orders', label: 'Queue' },
  { to: '/admin/sales', label: 'Sales' },
  { to: '/admin/renewals', label: 'Renewals' },
  { to: '/admin/reconciliation', label: 'Reconciliation' },
  { to: '/admin/inbox', label: 'Inbox' },
  { to: '/admin/agencies', label: 'Agencies' },
];

/**
 * **Setup**: the pages that change how the portal behaves rather than the day's
 * work. Emails keeps company with the rest of the settings now; Inbox stays on
 * the bar because it is read daily.
 */
const OPERATOR_SETUP: NavItem[] = [
  { to: '/admin/emails', label: 'Emails' },
  { to: '/admin/coupons', label: 'Coupons' },
  { to: '/admin/pricing', label: 'Pricing' },
  // C-132: what is reported to Meta for the ad. Checked now and then, not daily.
  { to: '/admin/meta', label: 'Meta' },
];

const PAYMENTS_SENTENCE: Record<'live' | 'test' | 'none', string> = {
  live: 'Payments: LIVE',
  test: 'Payments: TEST mode, no real money moves',
  none: 'Payments: not configured, buying is unavailable',
};

/**
 * The payments badge **on the bar**, shortened - the one-row ruling.
 *
 * **The LIVE warning is never only inside a menu**: this is on the bar at
 * every width of it, and the phone bar too. The word is short so the bar fits
 * on one row; the full sentence is its hover title and its screen-reader text,
 * and heads the Operator menu. "OFF" for no credentials, because what an
 * operator needs from that state is that buying does not work.
 */
function PaymentsBadge({ mode }: { mode: 'live' | 'test' | null }): React.JSX.Element {
  const state = mode ?? 'none';
  const style = {
    live: 'bg-danger-strong text-card',
    test: 'bg-warn-soft text-warn',
    none: 'bg-sunken text-fg-2',
  }[state];
  return (
    <span
      title={PAYMENTS_SENTENCE[state]}
      data-testid="payments-badge"
      className={`shrink-0 rounded-full px-2.5 py-0.5 font-mono text-xs font-semibold tracking-wide ${style}`}
    >
      <span aria-hidden="true">{{ live: 'LIVE', test: 'TEST', none: 'OFF' }[state]}</span>
      <span className="sr-only">{PAYMENTS_SENTENCE[state]}</span>
    </span>
  );
}

/**
 * "Am I taking real money right now?" - decision D-12, point 5.
 *
 * Before this, the only way to answer that was to open Railway and read an
 * environment variable, which is not a thing anybody does before clicking
 * around their own admin. So the answer sits in the header of every operator
 * page, taken from the key prefix the API actually booted with.
 *
 * **It is an indicator and nothing else.** Not a button, not a link, not a
 * toggle. Switching modes is a change to the Railway environment variables and
 * a restart, and a control here would suggest the portal can move its own money
 * settings, which it cannot and should not.
 *
 * The live state is loud on purpose. The test state says what test mode means
 * rather than just naming it, because "TEST" alone is exactly the word somebody
 * skims past while wondering why the money has not arrived.
 */
function PaymentsIndicator({ mode }: { mode: 'live' | 'test' | null }): React.JSX.Element {
  if (mode === 'live') {
    return (
      <span className="inline-block rounded-box bg-danger-strong px-2.5 py-0.5 text-xs font-semibold text-card">
        {PAYMENTS_SENTENCE.live}
      </span>
    );
  }
  if (mode === 'test') {
    return (
      <span className="inline-block rounded-box bg-warn-soft px-2.5 py-0.5 text-xs font-semibold text-warn">
        {PAYMENTS_SENTENCE.test}
      </span>
    );
  }
  /*
   * No credentials at all. Rare, and not one of the two states D-12 names, but
   * saying "TEST mode" here would be a lie of the useful-sounding kind: nothing
   * can be bought at all, in either mode.
   */
  return (
    <span className="inline-block rounded-box bg-sunken px-2.5 py-0.5 text-xs font-semibold text-fg-2">
      {PAYMENTS_SENTENCE.none}
    </span>
  );
}

/**
 * "Viewing as ..." - D-14 Item 7.3.
 *
 * **The only thing on screen that says this is not really the customer.**
 * Everything else about the page is the customer's view, which is the point,
 * so this has to be impossible to miss and has to carry the way out.
 *
 * The countdown is whatever `/api/me` last said, and it is deliberately not a
 * live timer: a number ticking down in the corner invites somebody to watch it
 * instead of the page, and the expiry is silent anyway (Item 7.4). It refreshes
 * whenever the session does.
 */
function ViewAsBanner({ viewing }: { viewing: ViewingAs }): React.JSX.Element {
  const { refresh } = useSession();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  /*
   * **A live countdown** - WAIT-B polish 9.
   *
   * It read whatever `/api/me` last said, so it sat on "30 min left" across
   * nine minutes of Craig's run and told him nothing. The expiry is computed
   * once from what the server sent and then ticked locally: one interval, once
   * a minute, which is as often as a figure in minutes can change.
   *
   * Still not the authority - the cookie's signed expiry is, and it is checked
   * on every request. This is a clock on the wall, and a clock on the wall that
   * is wrong is worse than none.
   */
  const [minutesLeft, setMinutesLeft] = useState(viewing.minutesLeft);
  const expiresAt = useRef(Date.now() + viewing.minutesLeft * 60_000);

  useEffect(() => {
    expiresAt.current = Date.now() + viewing.minutesLeft * 60_000;
    setMinutesLeft(viewing.minutesLeft);
  }, [viewing.minutesLeft]);

  useEffect(() => {
    const timer = setInterval(() => {
      setMinutesLeft(Math.max(0, Math.ceil((expiresAt.current - Date.now()) / 60_000)));
    }, 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  async function exit(): Promise<void> {
    setBusy(true);
    try {
      await stopViewAs();
      await refresh();
      void navigate('/admin/agencies');
    } finally {
      setBusy(false);
    }
  }

  /*
   * **The website's blue, full width** - ruling B. It was near-black under a
   * white header; the header is near-black now, so the same colour would have
   * merged into it, and this is the one band that must not be missed. Blue
   * fill with ground text is the website's own primary pairing (8.6:1).
   *
   * At 360px the sentence wraps and Exit drops under it, still at the right,
   * because the row wraps rather than squeezes.
   */
  return (
    <div className="border-b border-blue-ink bg-blue">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 sm:px-6">
        <p className="text-sm text-ground" role="status">
          <span className="font-semibold">Viewing as {viewing.agencyName}</span> (read-only).{' '}
          {minutesLeft} min left.
        </p>
        <button
          type="button"
          onClick={() => void exit()}
          disabled={busy}
          className="ml-auto rounded-control px-1 text-sm font-semibold text-ground underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ground disabled:opacity-60"
        >
          Exit
        </button>
      </div>
    </div>
  );
}

/**
 * "Your account is suspended" - D-14 Item 2.
 *
 * Renders from `/api/me`, on every page, above everything. A suspended agency
 * keeps read access on purpose (Item 2), so they are looking at their own
 * orders and sites with the buy and order controls gone, and a banner is the
 * only thing that tells them why. Without it the portal would simply appear
 * broken, and a customer who thinks the portal is broken opens a support ticket
 * or a chargeback rather than reading a refusal they never triggered.
 *
 * `statusNote` is the operator's own words and is shown verbatim when there is
 * one. When there is not, the first sentence stands on its own rather than
 * trailing a full stop into nothing.
 *
 * **Not rendered for `invited` or `closed`**, and there is nothing to render:
 * neither can hold a session, so neither can reach a page that draws this.
 */
function SuspendedBanner({ note }: { note: string | null }): React.JSX.Element {
  return (
    <div className="border-b border-warn-line bg-warn-soft">
      <div className="mx-auto max-w-5xl px-6 py-3">
        <p className="text-sm text-warn" role="status">
          <span className="font-semibold">Your account is suspended.</span>
          {note ? ` ${note}` : ' Get in touch and we will sort it out.'}
        </p>
      </div>
    </div>
  );
}

/**
 * The footer.
 *
 * The legal line used to be printed here on every page (ruling D-2 removed it).
 * What is left is a single discreet link to the Terms.
 *
 * **It goes to the website now** - WAIT-B round 2, and this one was left to me.
 * D-15 made the website the master and turned `/terms` into a page that says so
 * and links onward, so the footer was costing a click to reach a page with no
 * terms on it. Every other legal link in the portal already goes straight to
 * the website; this was the last one that did not.
 *
 * **The `/terms` route stays.** Old bookmarks, old emails and every acceptance
 * recorded before D-15 point at it, and a 404 under a legal statement is worse
 * than a page that forwards. What changed is only what the chrome offers.
 */
function Footer(): React.JSX.Element {
  return (
    <p className="text-xs text-fg-faint">
      <a {...NEW_TAB} href={LEGAL_URLS.terms} className="hover:text-fg-2 hover:underline">
        Terms
      </a>
    </p>
  );
}

/**
 * The dark top bar - Craig's ruling B, 29 September 2026.
 *
 * The website's `ground` with a `line` hairline, its wordmark, links in `ink-2`
 * and the active one in `blue`: the same bar a buyer was looking at on
 * entitylinks.com a moment before they signed in. The page under it stays
 * light, which is the half of the ruling that keeps a working screen readable.
 *
 * **Two layouts, chosen in script** (`useMediaQuery`). At 1024px and wider the
 * links sit in the bar and the rest wraps onto a second row if it has to, as it
 * always has for the operator. Narrower, the bar is the wordmark and a **Menu**
 * button, and everything else is in `MobileMenu`.
 */
export function AppShell({ children }: { children: ReactNode }): React.JSX.Element {
  const { session, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const wide = useMediaQuery(WIDE_NAV_QUERY);
  const operator = session?.user.role === 'admin';
  const links = operator ? OPERATOR_NAV : AGENCY_NAV;
  // Which drop-down is open. One at a time: opening one closes the other.
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname, location.search]);

  async function handleSignOut(): Promise<void> {
    await signOut();
    void navigate('/sign-in', { replace: true });
  }

  /*
   * **Sign out is hidden while viewing** - WAIT-B polish 11. It signed the
   * *operator* out entirely, which is a surprising thing for a button labelled
   * "Sign out" to do while the bar shows somebody else's account. Exit is on
   * the banner. That holds in the account menu and the phone panel alike.
   */
  const canSignOut = !session?.viewingAs;
  const agencyName = session?.agency?.name ?? '';
  const email = session?.user.email ?? '';
  const mode = session?.payments?.mode ?? null;

  const barLinks = (
    <nav className="flex min-w-0 items-center gap-0.5" aria-label="Main">
      {links.map((item) => {
        const current = isCurrent(item, location);
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={current ? 'page' : undefined}
            className={`shrink-0 rounded-control px-2.5 py-1.5 text-sm font-medium whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue ${
              current ? 'text-blue' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {item.label}
          </Link>
        );
      })}
      {operator ? (
        <DropdownMenu
          id="setup"
          label="Setup"
          active={holdsCurrent(OPERATOR_SETUP, location)}
          entries={OPERATOR_SETUP.map((item) => ({
            kind: 'link' as const,
            to: item.to,
            label: item.label,
            current: isCurrent(item, location),
          }))}
          openId={openMenu}
          onOpenChange={setOpenMenu}
        />
      ) : null}
    </nav>
  );

  const signOutEntry = { kind: 'action' as const, label: 'Sign out', onSelect: () => void handleSignOut() };

  const accountMenu = operator ? (
    <DropdownMenu
      id="account"
      label="Operator"
      align="right"
      header={
        <>
          <p className="truncate font-medium text-ink">{email}</p>
          <p className="mt-2">
            <PaymentsIndicator mode={mode} />
          </p>
        </>
      }
      entries={canSignOut ? [signOutEntry] : []}
      openId={openMenu}
      onOpenChange={setOpenMenu}
    />
  ) : (
    <DropdownMenu
      id="account"
      label={<span className="max-w-56 truncate">{agencyName}</span>}
      accessibleLabel={agencyName}
      active={holdsCurrent(AGENCY_ACCOUNT, location)}
      align="right"
      header={<p className="max-w-64 font-medium break-words text-ink">{agencyName}</p>}
      entries={[
        ...AGENCY_ACCOUNT.map((item) => ({
          kind: 'link' as const,
          to: item.to,
          label: item.label,
          current: isCurrent(item, location),
        })),
        ...(canSignOut ? [{ kind: 'divider' as const }, signOutEntry] : []),
      ]}
      openId={openMenu}
      onOpenChange={setOpenMenu}
    />
  );

  /*
   * The phone and tablet panel: every destination, no menu inside a menu.
   * The drop-downs' contents become labelled groups; who you are and Sign out
   * come last, as they do on the wide bar.
   */
  const mobile = operator ? (
    <MobileMenu links={links} groups={[{ label: 'Setup', links: OPERATOR_SETUP }]}>
      <PaymentsIndicator mode={mode} />
      <span className="w-full text-sm text-ink-2">{email}</span>
      {canSignOut ? (
        <Button variant="onDark" onClick={() => void handleSignOut()}>
          Sign out
        </Button>
      ) : null}
    </MobileMenu>
  ) : (
    <MobileMenu links={links} groups={[{ label: agencyName, links: AGENCY_ACCOUNT }]}>
      {canSignOut ? (
        <Button variant="onDark" onClick={() => void handleSignOut()}>
          Sign out
        </Button>
      ) : null}
    </MobileMenu>
  );

  return (
    <div className="min-h-full">
      <header className="relative z-30 border-b border-line bg-ground">
        {/*
          **One row, nothing wrapping** - Craig's ruling of 29 September, after
          WAIT-BMC's first look. The bar used to wrap its right-hand cluster
          onto a second row; now the rarely used pages live in drop-downs and
          `flex-nowrap` holds the row. Measured at 1024, 1280 and 1440 with a
          long agency name and the LIVE badge.
        */}
        <div className="mx-auto flex h-16 max-w-5xl flex-nowrap items-center gap-x-4 px-4 sm:px-6">
          <Wordmark />

          {wide ? (
            <>
              {barLinks}
              <div className="ml-auto flex min-w-0 shrink items-center gap-2">
                {operator ? <PaymentsBadge mode={mode} /> : null}
                {accountMenu}
              </div>
            </>
          ) : (
            <>
              {/* LIVE is never only inside a menu, on a phone either. */}
              {operator ? (
                <span className="ml-auto">
                  <PaymentsBadge mode={mode} />
                </span>
              ) : null}
              {mobile}
            </>
          )}
        </div>
      </header>

      {/*
        D-14 Item 7.3. Above the suspension banner, because when both are on
        screen the operator needs to know which account they are in before they
        read what is wrong with it.
      */}
      {session?.viewingAs ? <ViewAsBanner viewing={session.viewingAs} /> : null}

      {session?.agency?.status === 'suspended' ? (
        <SuspendedBanner note={session.agency.statusNote} />
      ) : null}

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">{children}</main>

      <footer className="mx-auto max-w-5xl px-4 pb-10 sm:px-6">
        <Footer />
      </footer>
    </div>
  );
}

/** Centred single-column layout used by every signed-out page. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}): React.JSX.Element {
  /*
   * Ruling B: the same dark band and wordmark as the signed-in bar, so the
   * first screen somebody sees after "Open an account" on the website looks
   * like where they came from. The form itself stays on the light canvas.
   */
  return (
    <div className="flex min-h-full flex-col">
      <header className="border-b border-line bg-ground">
        <div className="mx-auto flex min-h-16 max-w-5xl items-center px-4 py-3 sm:px-6">
          <Wordmark />
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-fg">{title}</h1>
            {subtitle ? <p className="mt-2 text-sm text-fg-muted">{subtitle}</p> : null}
          </div>

          <div className="rounded-panel bg-card p-6 shadow-sm ring-1 ring-rule sm:p-8">
            {children}
          </div>

          {footer ? <div className="mt-6 text-center text-sm text-fg-muted">{footer}</div> : null}

          <div className="mt-8 text-center">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}
